import { FlowComponent } from '../../../flow.min.js';
import styles from './styles.js';

const template = FlowComponent.html`
    <button type="${'type'}" class="w-full flex items-center justify-center border border-transparent font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${'variant-class'}">
        <slot></slot>
        <span class="label-text">${'label'}</span>
        <slot name="icon"></slot>
    </button>
`;

/*
El componente asumirá por defecto:
1.  Color: primary (Azul)
2.  Tamaño: Estándar (py-2 px-4 text-sm)
3.  Forma: Redondeada (rounded-lg)
4.  Estilo: Sólido (Fondo azul, texto blanco)
*/

export class FlowButton extends FlowComponent {
    static get styles() { return styles; }
    static get template() { return template; }

    static get observedAttributes() {
        return ['variant', 'type', 'label', 'loading', 'small', 'big', 'light', 'dark', 'flat', 'variant-class', 'w-auto'];
    }

    // Mapas de estilos completos para asegurar que Tailwind genere las clases
    #styleMap = {
        primary: {
            default: 'bg-brilliant-azure-500 hover:bg-brilliant-azure-600 text-white focus:ring-brilliant-azure-500',
            light: 'bg-brilliant-azure-100 hover:bg-brilliant-azure-200 text-brilliant-azure-800 focus:ring-brilliant-azure-500',
            dark: 'bg-ink-black-400 hover:bg-ink-black-500 text-white focus:ring-ink-black-400'
        },
        secondary: {
            default: 'bg-graphite-500 hover:bg-graphite-600 text-white focus:ring-graphite-500',
            light: 'bg-graphite-100 hover:bg-graphite-200 text-graphite-800 focus:ring-graphite-500',
            dark: 'bg-graphite-800 hover:bg-graphite-900 text-white focus:ring-graphite-500'
        },
        danger: {
            default: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
            light: 'bg-red-100 hover:bg-red-200 text-red-800 focus:ring-red-500',
            dark: 'bg-red-800 hover:bg-red-900 text-white focus:ring-red-500'
        },
        success: {
            default: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500',
            light: 'bg-green-100 hover:bg-green-200 text-green-800 focus:ring-green-500',
            dark: 'bg-green-800 hover:bg-green-900 text-white focus:ring-green-500'
        },
        warning: {
            default: 'bg-yellow-600 hover:bg-yellow-700 text-white focus:ring-yellow-500',
            light: 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800 focus:ring-yellow-500',
            dark: 'bg-yellow-800 hover:bg-yellow-900 text-white focus:ring-yellow-500'
        }
    };

    onMount() {
        super.onMount();
        this.#updateClasses();
    }

    onAttributeChanged(name, old, val) {
        // Reaccionamos a cualquier cambio en atributos relevantes
        if (['variant', 'small', 'big', 'light', 'dark', 'flat'].includes(name)) {
            this.#updateClasses();
        }
        if (name === 'loading') {
            this.#toggleLoading(val !== null);
        }
    }

    set loading(val) {
        if (val) {
            this.setAttribute('loading', '');
        } else {
            this.removeAttribute('loading');
        }
    }

    get loading() {
        return this.hasAttribute('loading');
    }

    #toggleLoading(isLoading) {
        const btn = this.root.querySelector('button');
        if (!btn) return;

        if (isLoading) {
            btn.disabled = true;
            btn.classList.add('cursor-not-allowed', 'opacity-75');
            
            // Check if spinner already exists to avoid duplicates
            if (!this.root.querySelector('.spinner')) {
                const spinner = document.createElement('div');
                spinner.className = 'spinner mr-2';
                spinner.innerHTML = `
                    <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                `;
                btn.insertBefore(spinner, btn.firstChild);
            }
        } else {
            btn.disabled = false;
            btn.classList.remove('cursor-not-allowed', 'opacity-75');
            const spinner = this.root.querySelector('.spinner');
            if (spinner) spinner.remove();
        }
    }

    #updateClasses() {
        // 1. Resolver variante y modo
        const variant = this.getAttribute('variant') || 'primary';
        let mode = 'default';
        if (this.hasAttribute('light')) mode = 'light';
        else if (this.hasAttribute('dark')) mode = 'dark';

        // 2. Obtener clases base (Safelist implícito)
        const themeStyles = this.#styleMap[variant] || this.#styleMap['primary'];
        const colorClasses = themeStyles[mode];

        // 3. Resolver el Tamaño (Small/Big/Default)
        let sizeClasses = 'py-2 px-4 text-sm'; // Default
        if (this.hasAttribute('small')) {
            sizeClasses = 'py-1 px-2 text-xs';
        } else if (this.hasAttribute('big')) {
            sizeClasses = 'py-3 px-6 text-base';
        }

        // 4. Resolver la Forma (Flat/Rounded)
        const shapeClasses = this.hasAttribute('flat') ? 'rounded-none' : 'rounded-lg';

        // 5. Layout (Gap entre texto e icono)
        const layoutClasses = 'gap-2';

        // 6. Ancho (Full por defecto, o auto si se especifica)
        const widthClass = this.hasAttribute('w-auto') ? 'w-auto' : 'w-full';

        // Combinar todo
        const finalClass = `${widthClass} ${sizeClasses} ${colorClasses} ${shapeClasses} ${layoutClasses}`;
        
        const btn = this.root.querySelector('button');
        if (btn) {
            btn.className = `flex items-center justify-center border border-transparent font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${finalClass}`;
        }

        this.setAttribute('variant-class', finalClass);
    }
}

customElements.define('flow-button', FlowButton);
